import { useState, useEffect } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { useWebSocket } from "../contexts/WebSocketContext";
import { notificationAPI } from "../services/api";
import {
  Bell,
  Check,
  Trash2,
  AlertCircle,
  CheckCircle,
  Clock,
  Zap,
  MessageSquare,
  UserPlus,
  RefreshCw,
  Wifi,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Link } from "react-router-dom";

interface Notification {
  id: string;
  ticketId: string | null;
  userId: string;
  type:
    | "ticket_created"
    | "ticket_assigned"
    | "status_updated"
    | "ticket_resolved"
    | "comment_added"
    | "feedback_request"
    | "ticket_escalated"
    | "ticket_reassigned";
  message: string;
  isRead: boolean;
  isSent: boolean;
  deliveryStatus: "pending" | "sent" | "failed";
  createdAt: string;
}

export default function Notifications() {
  const {
    notifications: wsNotifications,
    isConnected,
    markAsRead: wsMarkAsRead,
  } = useWebSocket();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotifications();
  }, [filter]);

  useEffect(() => {
    if (wsNotifications.length > 0) {
      setNotifications((prev) => {
        const existingIds = new Set(prev.map((n) => n.id));
        const newNotifications = wsNotifications.filter(
          (n) => !existingIds.has(n.id),
        );
        return [...newNotifications, ...prev];
      });
    }
  }, [wsNotifications]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const isRead = filter === "unread" ? false : undefined;
      const response = await notificationAPI.list(1, 20, isRead);

      if (response.success || Array.isArray(response)) {
        const notifList = Array.isArray(response)
          ? response
          : response.data?.notifications ||
            response.data?.items ||
            response.data ||
            [];
        setNotifications(notifList);
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to load notifications";

      if (!/session expired|unauthorized|401/i.test(message)) {
        toast.error("Failed to load notifications");
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredNotifications = notifications;

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const markAsRead = async (id: string) => {
    try {
      await notificationAPI.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
      );
      wsMarkAsRead(id);
      toast.success("Marked as read");
    } catch (error) {
      toast.error("Failed to mark as read");
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      toast.success("All notifications marked as read");
    } catch (error) {
      toast.error("Failed to mark all as read");
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      await notificationAPI.delete(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      toast.success("Notification deleted");
    } catch (error) {
      toast.error("Failed to delete notification");
    }
  };

  const getNotificationIcon = (type: Notification["type"]) => {
    switch (type) {
      case "ticket_created":
        return <Bell className="w-5 h-5 text-blue-500" />;
      case "ticket_assigned":
        return <UserPlus className="w-5 h-5 text-green-500" />;
      case "status_updated":
        return <RefreshCw className="w-5 h-5 text-yellow-500" />;
      case "ticket_resolved":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "comment_added":
        return <MessageSquare className="w-5 h-5 text-blue-500" />;
      case "feedback_request":
        return <Clock className="w-5 h-5 text-purple-500" />;
      case "ticket_escalated":
        return <Zap className="w-5 h-5 text-red-500" />;
      case "ticket_reassigned":
        return <RefreshCw className="w-5 h-5 text-orange-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 md:p-8 max-w-5xl mx-auto">
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-3">
                <Bell className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
                Notifications
                {isConnected && (
                  <span className="flex items-center gap-2 px-3 py-1 bg-green-500/10 text-green-500 text-xs font-medium rounded-full">
                    <Wifi className="w-3 h-3" />
                    Live
                  </span>
                )}
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-1">
                {isConnected
                  ? "Real-time updates enabled"
                  : "Stay updated with ticket notifications"}
                Stay updated on your tickets and system events
              </p>
            </div>
            {unreadCount > 0 && (
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                  {unreadCount} unread
                </span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-2 p-1 bg-muted rounded-lg">
              <button
                onClick={() => setFilter("all")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                  filter === "all"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                All ({notifications.length})
              </button>
              <button
                onClick={() => setFilter("unread")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                  filter === "unread"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Unread ({unreadCount})
              </button>
            </div>

            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors text-sm font-medium cursor-pointer"
              >
                <Check className="w-4 h-4" />
                Mark all read
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, idx) => (
              <div
                key={idx}
                className="bg-card border border-border rounded-lg p-4 animate-pulse"
              >
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-muted rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredNotifications.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-lg p-8 sm:p-12 text-center"
          >
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Bell className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {filter === "unread"
                ? "No unread notifications"
                : "No notifications yet"}
            </h3>
            <p className="text-muted-foreground text-sm">
              {filter === "unread"
                ? "You're all caught up! Check back later for updates."
                : "When you create or interact with tickets, you'll see notifications here."}
            </p>
          </motion.div>
        ) : (
          <div className="space-y-2">
            {filteredNotifications.map((notification, index) => (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`group bg-card border rounded-lg hover:shadow-lg transition-all ${
                  notification.isRead
                    ? "border-border"
                    : "border-primary/50 bg-primary/5"
                }`}
              >
                <div className="p-4 flex gap-4">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      notification.isRead ? "bg-muted" : "bg-primary/10"
                    }`}
                  >
                    {getNotificationIcon(notification.type)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p
                          className={`text-sm ${
                            notification.isRead
                              ? "text-muted-foreground"
                              : "text-foreground font-medium"
                          }`}
                        >
                          {notification.message}
                        </p>
                        {notification.ticketId && (
                          <Link
                            to={`/dashboard/ticket/${notification.ticketId}`}
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
                          >
                            View Ticket
                            <span className="font-mono">
                              {notification.ticketId}
                            </span>
                          </Link>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {getTimeAgo(notification.createdAt)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {!notification.isRead && (
                      <button
                        onClick={() => markAsRead(notification.id)}
                        className="p-2 rounded-lg hover:bg-green-500/10 text-green-500 transition-colors cursor-pointer"
                        title="Mark as read"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => deleteNotification(notification.id)}
                      className="p-2 rounded-lg hover:bg-red-500/10 text-red-500 transition-colors cursor-pointer"
                      title="Delete notification"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
